/*
 * Naming-gap override (2026-10-06) — shared by the single-run wizard footer
 * (LaunchWizard) and the per-group preview (GroupPreviewSection).
 *
 * A ticket whose master-sheet ad name has blank cells is prepped but HELD: its
 * ads launch only when the operator ticks "Launch anyway" for that ticket. The
 * server enforces the same rule at confirm, so these helpers only keep the UI
 * honest about what will launch.
 */
import type { PreviewTicketEntry } from "../../api/client";

/** Ready tickets carrying a naming gap. */
export function heldTickets(tickets: PreviewTicketEntry[]): PreviewTicketEntry[] {
  return tickets.filter((t) => t.ok && t.naming_gap != null);
}

/** Ads that cannot launch yet: on a held ticket the operator has NOT overridden. */
export function lockedAdIds(
  tickets: PreviewTicketEntry[],
  overrides: ReadonlySet<string>,
): Set<string> {
  const out = new Set<string>();
  for (const t of heldTickets(tickets)) {
    if (overrides.has(t.ticket_id)) continue;
    for (const a of t.ads) out.add(a.id);
  }
  return out;
}

/** Ads the operator may select / that an "all" launch covers. */
export function launchableAdIds(
  tickets: PreviewTicketEntry[],
  overrides: ReadonlySet<string>,
): string[] {
  const locked = lockedAdIds(tickets, overrides);
  return tickets
    .filter((t) => t.ok)
    .flatMap((t) => t.ads.map((a) => a.id))
    .filter((id) => !locked.has(id));
}

/** "1 blank naming cell: DESIGNER" / "3 blank naming cells: A, B, C". */
export function namingGapLabel(gap: { count: number; cells: string[] }): string {
  const n = gap.count;
  const head = `${n} blank naming cell${n !== 1 ? "s" : ""}`;
  return gap.cells.length > 0 ? `${head}: ${gap.cells.join(", ")}` : head;
}

/**
 * Next override set + selection after toggling one ticket. Removing an
 * override also drops that ticket's ads from the selection, so a re-locked ad
 * can never ride along in `selected_ad_ids` (the server would reject it).
 */
export function toggleOverride(
  tickets: PreviewTicketEntry[],
  overrides: ReadonlySet<string>,
  selected: ReadonlySet<string>,
  ticketId: string,
  checked: boolean,
): { overrides: Set<string>; selected: Set<string> } {
  const nextOverrides = new Set(overrides);
  const nextSelected = new Set(selected);
  if (checked) {
    nextOverrides.add(ticketId);
  } else {
    nextOverrides.delete(ticketId);
    const t = tickets.find((x) => x.ticket_id === ticketId);
    for (const a of t?.ads ?? []) nextSelected.delete(a.id);
  }
  return { overrides: nextOverrides, selected: nextSelected };
}
